<template>
    <b-container fluid>
        <b-form-group class="form-spacing">
            <!-- Send to CRSE checkbox -->
            <b-row class="my-3" v-if="this.getSelectedRule.exceptionLevel > 1">
                <b-col cols="2">
                    <label>Send to CRSE?</label>
                </b-col>
                <b-col cols="3">
                    <b-form-checkbox v-model="getCurrentOverride.sendToCrse" :disabled="disabled" data-test="sendToCrse"></b-form-checkbox>
                </b-col>
            </b-row>
            <!-- Guideline -->
            <b-row class="my-3" col>
                <b-col cols="2">
                    <label>Guideline Requirement:</label>
                </b-col>
                <b-col cols="8">
                    <b-form-textarea rows="7" :value="getSelectedRule.message" disabled data-test="guideline"></b-form-textarea>
                </b-col>
            </b-row>
            <!-- Value table -->
            <b-row class="my-3" col>
                <b-col cols="2">
                    <label>Actual Value(s):</label>
                </b-col>
                <b-col cols="8">
                    <el-table
                        class="table"
                        :data="getFields"
                        header-row-class-name="thead-light"
                        empty-text="No scenario has been run or saved."
                        border
                        style="width: 99%"
                    >
                        <el-table-column label="Field" prop="name" style="border-style: solid">
                            <template v-slot="{ row }">
                                <div class="table-row-name">{{ row.name }}</div>
                            </template>
                        </el-table-column>

                        <el-table-column label="Value" prop="value">
                            <template v-slot="{ row }" class="pl-5">
                                <div class="pl-2">
                                    {{ row.value }}
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>
                </b-col>
            </b-row>
            <!-- Exception Category -->
            <b-row class="mt-3" v-if="this.getSelectedRule.exceptionLevel >= 2">
                <b-col cols="2">
                    <label>Category:</label>
                </b-col>
                <b-col cols="4">
                    <b-form-select
                        :options="exceptionCategories"
                        :value="getCurrentOverride.category"
                        :state="inputState('category')"
                        :disabled="disabled"
                        @input="
                            editOverride({ prop: 'category', value: $event });
                            v$.getCurrentOverride['category'].$touch();
                            // if the type has not been selected before do not highlight the field as invalid
                            if (getCurrentOverride.type) { editOverride({ prop: 'type', value: null }) };
                            setExceptionTypes(getCurrentOverride.category);
                        "
                        data-test="category"
                    >
                    </b-form-select>
                </b-col>
            </b-row>
            <!-- Exception Type -->
            <b-row class="mt-3" v-if="this.getSelectedRule.exceptionLevel >= 2">
                <b-col cols="2">
                    <label>Type:</label>
                </b-col>
                <b-col cols="4">
                    <b-form-select 
                        :options="currentExceptionTypes" 
                        :value="getCurrentOverride.type"
                        :disabled="!getCurrentOverride.category || disabled" 
                        @input="
                            editOverride({ prop: 'type', value: $event });
                            v$.getCurrentOverride['type'].$touch();
                            setPassingCompFactorLength();
                        "
                        :state="inputState('type')"
                        data-test="type"
                    > </b-form-select>
                </b-col>
            </b-row>
            <!-- Comment -->
            <b-row class="mt-3">
                <b-col cols="2">
                    <label>Comment:</label>
                </b-col>
                <b-col cols="8">
                    <b-textarea
                        rows="3"
                        :value="getCurrentOverride.comment"
                        @input="
                            editOverride({ prop: 'comment', value: $event });
                            v$.getCurrentOverride['comment'].$touch();
                        "
                        :disabled="disabled"
                        :state="inputState('comment')"
                        data-test="comment"
                    ></b-textarea>
                </b-col>
            </b-row>
            <!-- Granted By-->
            <b-row class="my-3" col v-if="this.getSelectedRule.exceptionLevel >= 2">
                <b-col cols="2">
                    <label>Granted By:</label>
                </b-col>
                <b-col cols="3">
                    <b-form-select 
                        :options="getUsernames"
                        :value="getCurrentOverride.grantedByUsername"
                        data-test="grantedByUsername"
                        :disabled="disabled"
                        @input="
                            editOverride({ prop: 'grantedByUsername', value: $event });
                            v$.getCurrentOverride['grantedByUsername'].$touch();
                            editOverride({ prop: 'grantedByUserId', value: getUserId($event) })
                        "
                        :state="inputState('grantedByUsername')"
                    ></b-form-select>
                </b-col>
            </b-row>
            <!-- Date granted -->
            <b-row class="my-3" col v-if="this.getSelectedRule.exceptionLevel >= 2">
                <b-col cols="2">
                    <label>Date Granted:</label>
                </b-col>
                <b-col cols="3">
                    <b-form-input 
                        type="date" 
                        :value="getCurrentOverride.grantedByDate" 
                        data-test="grantedByDate"
                        :disabled="disabled"
                        @input="
                            editOverride({ prop: 'grantedByDate', value: $event });
                            v$.getCurrentOverride['grantedByDate'].$touch();
                        "
                        :state="inputState('grantedByDate')"
                    ></b-form-input>
                </b-col>
            </b-row>
            <!-- Created by -->
            <b-row class="my-3" col>
                <b-col cols="2">
                    <label>Created By:</label>
                </b-col>
                <b-col cols="3">
                    <b-form-input :value="getCurrentOverride.username" disabled data-test="username"></b-form-input>
                </b-col>
            </b-row>
            <!-- Created date -->
            <b-row class="my-3">
                <b-col cols="2">
                    <label>Date Created:</label>
                </b-col>
                <b-col cols="3">
                    <b-form-input 
                        :value="getCurrentOverride.created" 
                        disabled 
                        data-test="created"
                        type="date"
                    ></b-form-input>
                </b-col>
            </b-row>
        </b-form-group>
    </b-container>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { mapGetters, mapMutations } from "vuex";
import { required, requiredIf } from "vuelidate/lib/validators";
import { Table, TableColumn } from "element-ui";
import { addFieldToList } from "@/utils/FieldFormatter.js";

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    data() {
        return {
            currentExceptionTypes: [],
        };
    },
    components: {
        ["ElTable"]: Table,
        ["ElTableColumn"]: TableColumn,
    },
    props: {
        disabled: Boolean,
    },
    computed: {
        ...mapGetters(["getCurrentOverride", "getSelectedRule", "getScenario", "getPassingCompFactorLength", "getUsers", "getUsernames"]),
        exceptionCategories() {
            // map the categories object array to be an array of just category names
            return this.getScenario.categories.map((i) => i.category);
        },
        getFields() {
            let fields = [];
            // generate the message to be displayed for each field
            for (let field of this.getSelectedRule.fields) {
                // add the fields recursively if the field has children
                if (field.children) {
                    fields = addFieldToList(field, fields, []);
                // if the field does not have children just add the name and value to the list
                } else {
                    fields.push({
                        name: field.name,
                        value: field.value
                    });
                }
            }
            return fields;
        }
    },
    methods: {
        ...mapMutations(["editOverride", "setPassingCompFactorLength"]),
        inputState(prop) {
            return !this.v$.getCurrentOverride[prop].$error ? null : !this.v$.getCurrentOverride[prop].$invalid;
        },
        setExceptionTypes(category) {
            // sets the exception types to be the types of the currently selected exception category
            let exceptionIdentifier;
            if (category) {
                exceptionIdentifier = this.getScenario.categories.find((i) => i.category === category);
            // if no category is selected then return an empty types array
            } else {
                exceptionIdentifier = { types: [] };
            }
            this.currentExceptionTypes = exceptionIdentifier.types;
        },
        twoRemainingFactors() {
            // validation method to check if there are two or more comp factors given that a rule's exception level is 2 or more
            let twoPassingCompFactorsExist;
            // levels below 2 don't require compensating factors
            if (this.getSelectedRule.exceptionLevel < 2) {
                twoPassingCompFactorsExist = true;
            } else if (this.getPassingCompFactorLength >= 2) {
                twoPassingCompFactorsExist = true;
            } else {
                twoPassingCompFactorsExist = false;
            }
            return twoPassingCompFactorsExist;
        },
        getUserId(username) {
            let selectedUser = this.getUsers.find(u => u.username === username);
            return selectedUser.userId;
        }
    },
    mounted() {
        // set the array of exception types
        this.setExceptionTypes(this.getCurrentOverride.category)
    },
    validations() {
        return {
            getCurrentOverride: {
                comment: {
                    required,
                },
                category: {
                    required: requiredIf(function() {
                        return this.getSelectedRule.exceptionLevel >= 2;
                    })
                },
                type: {
                    required: requiredIf(function() {
                        return this.getSelectedRule.exceptionLevel >= 2;
                    })
                },
                grantedByDate: {
                    required: requiredIf(function() {
                        return this.getSelectedRule.exceptionLevel >= 2;
                    })
                },
                grantedByUsername: {
                    required: requiredIf(function() {
                        return this.getSelectedRule.exceptionLevel >= 2;
                    })
                },
            },
            numberOfFactors: {
                required: function () {
                    return this.twoRemainingFactors();
                }
            }
        };
    },
};
</script>

<style scoped>
.form-spacing {
    margin-top: -1.5rem;
    margin-bottom: 0;
}

.btn-ok {
    background-color: #00afef !important;
}

.custom-control-input:checked ~ .custom-control-label::before {
    color: #006891;
    border-color: #006891;
    background-color: white;
    box-shadow: 0 3px 2px rgb(233 236 239 / 5%);
}

</style>

<style>
</style>